import { useMobileLayout } from "@/hooks/useAppMedia";
import { useProjectContext } from "@/providers";
import { useUpdateProjectStatusKpiMutation } from "@/services";
import * as S from "@/sharedStyled";
import { Radio, RadioChangeEvent, Space } from "antd";
import { arrayOf, bool, func, string } from "prop-types";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

interface IProps {
  isOpen: boolean;
  onCancel: () => void;
  availableStatuses: string[];
  kpi: string;
  currentStatus: string;
}

const EditStatusKpiModal = ({
  isOpen,
  onCancel,
  availableStatuses,
  kpi,
  currentStatus,
}: IProps) => {
  const { projectId } = useParams();
  const intl = useIntl();
  const isMobileLayout = useMobileLayout();
  const [form, setForm] = useState(null);
  const { setProject } = useProjectContext();

  const [updateProjectStatusKpi] = useUpdateProjectStatusKpiMutation();

  const handleStatusChange = (): void => {
    if (!form) {
      onCancel();
      return;
    }
    updateProjectStatusKpi({
      projectId,
      kpi,
      status: form,
    })
      .unwrap()
      .then((project) => {
        setProject(project);
        onCancel();
        toast.success(intl.formatMessage({ id: "editKpiStatusModal.saved" }));
      })
      .catch(() => {
        onCancel();
        toast.error(intl.formatMessage({ id: "editKpiStatusModal.error" }));
      });
  };

  const onChange = (e: RadioChangeEvent): void => {
    setForm(e.target.value);
  };

  return (
    <S.ResponsiveModal
      $mobile={isMobileLayout}
      maskClosable={false}
      title={intl.formatMessage(
        {
          id: "editKpiStatusModal.title",
        },
        { kpiLabel: kpi }
      )}
      onOk={handleStatusChange}
      onCancel={onCancel}
      open={isOpen && availableStatuses?.length > 0}
      okText={intl.formatMessage({
        id: "editKpiStatusModal.change",
      })}
    >
      <Radio.Group
        data-test-id="edit-status-kpis-modal-radio"
        onChange={onChange}
        value={
          availableStatuses.includes(currentStatus) && !form
            ? currentStatus
            : form
        }
      >
        <Space direction="vertical">
          {availableStatuses?.map((status, index) => (
            <Radio key={index} value={status}>
              {status}
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    </S.ResponsiveModal>
  );
};

EditStatusKpiModal.propTypes = {
  isOpen: bool,
  onCancel: func.isRequired,
  availableStatuses: arrayOf(string),
  kpi: string,
  currentStatus: string,
};

export { EditStatusKpiModal };
